import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { getObservableFromAsyncValueSafe, isAsyncValueProvider } from '../../../../models/async-value';
import { AbstractField } from '../../models/field-configs/abstract-field';
import { FieldInputNumberConfig } from '../../models/field-configs/field-input-number-config.interface';
import { RtlDetectorService } from '@ordersnproducts/modules/shared/services';
import { CapacityEnum } from '@ordersnproducts/modules/shared/constants/capacity';
import { StatusMessageParameter } from '../../models/field-configs/field-config.interface';

@Component({
  selector: 'form-input-number',
  styleUrls: ['form-input-number.component.scss'],
  templateUrl: 'form-input-number.component.html',
})
export class FormInputNumberComponent extends AbstractField implements OnInit, OnDestroy {
  public config: FieldInputNumberConfig;
  public group: UntypedFormGroup;
  public fieldControl: UntypedFormControl;

  public isRequired = false;
  public minVal = CapacityEnum.MIN_VALUE_INPUT_NUMBER;
  public maxVal = CapacityEnum.RMS_MAX_CAPACITY_DEFAULT;
  public message: string;
  public errorMessage: string;
  public controlStatusMessage$ = new BehaviorSubject('');
  public instruction$ = new BehaviorSubject(null);
  public formControlStatus$ = new BehaviorSubject('regular');
  public step = 1;
  public decimals: number;
  public decimalSeparator: string;
  public thousandSeparator: string;
  public ngDestroy = new Subject();
  public rtl = false;
  public statusMessage: string;
  public statusMessageParameter: StatusMessageParameter;
  public statusMessageParameter$ = new BehaviorSubject<StatusMessageParameter>(null);

  private decimalPrecision: number;

  constructor(
    private translate: TranslateService,
    private rtlDetect: RtlDetectorService,
  ) {
    super();
    this.decimalPrecision = Number(this.translate.instant('global.decimalNumbers'));
    this.decimalSeparator = this.translate.instant('global.decimalSeparator');
    this.thousandSeparator = this.translate.instant('global.thousandSeparator');
  }

  increment() {
    if (this.maxVal > this.getValue()) {
      this.setValue(this.getValue() + this.step);
    }
  }

  decrement() {
    if (this.minVal < this.getValue()) {
      this.setValue(this.getValue() - this.step);
    }
  }

  setValue(value: number) {
    this.group.controls[this.config.name].patchValue(value);
  }

  getValue() {
    return this.group.value[this.config.name];
  }

  ngOnInit(): void {
    this.rtl = this.rtlDetect.activeLangRTL();
    this.fieldControl = this.getControl() as UntypedFormControl;
    if (this.config && this.config.min && typeof this.config.min === 'number') {
      this.minVal = this.config.min as number;
    }
    if (this.config && this.config.max && typeof this.config.max === 'number') {
      this.maxVal = this.config.max as number;
    }
    this.initLoadingState();

    if (this.getValue() === undefined || isAsyncValueProvider(this.config.value)) {
      this.setValue(0);
      getObservableFromAsyncValueSafe(this.config.value)
        .pipe(
          take(1),
          map((value) => {
            if (value === null || value === undefined) {
              return null;
            }
            return +value.toFixed(this.decimalPrecision);
          }),
        )
        .subscribe((n) => this.setValue(n));
    }

    if (this.config.step) {
      getObservableFromAsyncValueSafe(this.config.step)
        .pipe(
          takeUntil(this.ngDestroy),
          filter((n) => n !== undefined && n !== null),
          map((n) => +n.toFixed(this.decimalPrecision)),
        )
        .subscribe((n) => (this.step = n));
    }

    if (this.config.min) {
      getObservableFromAsyncValueSafe(this.config.min)
        .pipe(
          takeUntil(this.ngDestroy),
          filter((numberMin) => numberMin !== undefined && numberMin !== null),
          map((numberMin) => +numberMin.toFixed(this.decimalPrecision)),
        )
        .subscribe((numberMin) => {
          this.minVal = numberMin;
          if (this.getValue() < numberMin) {
            this.setValue(numberMin);
          }
        });
    }

    if (this.config.max) {
      getObservableFromAsyncValueSafe(this.config.max)
        .pipe(
          takeUntil(this.ngDestroy),
          filter((numberMax) => numberMax !== undefined && numberMax !== null),
          map((numberMax) => +numberMax.toFixed(this.decimalPrecision)),
        )
        .subscribe((numberMax) => {
          this.maxVal = numberMax;
          if (this.getValue() > numberMax) {
            this.setValue(numberMax);
          }
        });
    }

    if (this.config.message) {
      getObservableFromAsyncValueSafe(this.config.message)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((message) => (this.message = message));
    }

    if (this.config.instruction) {
      getObservableFromAsyncValueSafe(this.config.instruction)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((instruction) => this.instruction$.next(instruction));
    }

    if (this.config.errorMessage) {
      getObservableFromAsyncValueSafe(this.config.errorMessage)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((errorMessage) => this.controlStatusMessage$.next(errorMessage));
    }

    if (this.config.decimals) {
      getObservableFromAsyncValueSafe(this.config.decimals)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((decimals) => (this.decimals = decimals));
    }

    if (this.config.blocked != undefined) {
      getObservableFromAsyncValueSafe(this.config.blocked)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((blocked) => {
          if (blocked) {
            this.getControl().disable();
          } else {
            this.getControl().enable();
          }
        });
    }
    if (this.config.warningMessage) {
      getObservableFromAsyncValueSafe(this.config.warningMessage)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((warningMessage) => {
          if (warningMessage !== '') {
            this.formControlStatus$.next('warning');
          } else {
            this.formControlStatus$.next('regular');
          }

          this.controlStatusMessage$.next(warningMessage);
        });
    }
    this.isRequired = this.hasRequiredField(this.group.get(this.config.name));
    if (this.config.statusMessage) {
      getObservableFromAsyncValueSafe(this.config.statusMessage)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((statusMessage) => {
          this.statusMessage = statusMessage;
          this.controlStatusMessage$.next(statusMessage);
        });
    }

    if (this.config.statusMessageParameter) {
      getObservableFromAsyncValueSafe(this.config.statusMessageParameter)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((statusMessageParameter) => {
          this.statusMessageParameter = statusMessageParameter;
          this.statusMessageParameter$.next(statusMessageParameter);
        });
    }

    this.fieldControl.statusChanges.pipe(takeUntil(this.ngDestroy)).subscribe(() => {
      if (this.fieldControl.invalid && this.fieldControl.hasError('errorMessage') && !this.fieldControl.untouched) {
        if (this.fieldControl.errors['errorMessage']) {
          this.controlStatusMessage$.next(this.fieldControl.errors['errorMessage']);
        }
        if (this.fieldControl.errors['parameters']) {
          this.statusMessageParameter$.next(this.fieldControl.errors['parameters']);
        }
        this.formControlStatus$.next('error');
      } else if (this.config.errorMessage && this.fieldControl.hasError('min')) {
        this.formControlStatus$.next('error');
      } else if (this.config.statusMessage) {
        this.formControlStatus$.next('regular');
        this.controlStatusMessage$.next(this.statusMessage);
        this.statusMessageParameter$.next(this.statusMessageParameter);
      } else {
        this.controlStatusMessage$.next('');
        this.formControlStatus$.next('regular');
      }
    });
  }

  public checkForNull(value): void {
    const isEmpty = value === null || value === undefined || value === '';
    if (isEmpty && !this.config.nullable) {
      setTimeout(() => {
        this.fieldControl.setErrors({ required: true });
      }, 0);
    }
  }

  private initLoadingState() {
    if (this.config.loading) {
      getObservableFromAsyncValueSafe(this.config.loading)
        .pipe(takeUntil(this.ngDestroy))
        .subscribe((value) => {
          if (value) {
            this.getControl().disable({ onlySelf: true, emitEvent: false });
          } else {
            this.getControl().enable({ onlySelf: true, emitEvent: false });
          }
        });
    }
  }

  /**
   * This implementation is used to handle the input change event
   * Will help to detect what the user is typing in the input field
   * and set the value to the form control, this can be used in validators
   */
  onInputChange(value) {
    if (this.config.enableInputAsValue) {
      this.setValue(value);
    }
  }

  ngOnDestroy() {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
